import { DEFAULT_KEY_RESULT, isUntouchedKeyResult } from './validation';

import type { KeyResultFormData, ObjectiveFormData } from './validation';
import type { ObjectiveStatusValue } from '@/constants/ObjectiveStatus';
import type { CreateObjectiveRequest, KeyResult, ObjectiveOrDraft } from '@/types/objectives';

import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { ObjectiveStatus } from '@/constants/ObjectiveStatus';
import { ObjectiveType } from '@/constants/ObjectiveType';
import { clampToKeyResultRange } from '@/utils/objectiveProgress';

/** The fields an AI draft proposes for a key result; a change to any of them is the user's own work. */
const isUneditedDraftRow = (keyResult: KeyResultFormData, applied: KeyResultFormData): boolean =>
    !keyResult.id &&
    keyResult.title === applied.title &&
    keyResult.unit === applied.unit &&
    keyResult.startValue === applied.startValue &&
    keyResult.targetValue === applied.targetValue &&
    keyResult.currentValue === applied.currentValue;

/**
 * True while nothing of the user's own is among the key results: every row is still blank, or is
 * one the last AI draft filled and nobody edited since. A regenerated draft may then replace them
 * all; a row the user wrote or reworked keeps the whole list out of the draft's reach.
 */
export const hasOnlyReplaceableKeyResults = (
    keyResults: KeyResultFormData[],
    appliedDraftKeyResults: readonly KeyResultFormData[],
): boolean =>
    keyResults.every(
        (keyResult) =>
            keyResult.title.trim() === '' ||
            appliedDraftKeyResults.some((applied) => isUneditedDraftRow(keyResult, applied)),
    );

export const emptyObjectiveFormValues = (prefilledEmployeeId?: string): ObjectiveFormData => ({
    title: '',
    description: '',
    pillarIds: null,
    employeeIds: prefilledEmployeeId ? [prefilledEmployeeId] : [],
    participantAttributes: [],
    competencyIds: [],
    startDate: '',
    endDate: '',
    keyResults: [{ ...DEFAULT_KEY_RESULT }],
    reminders: [],
});

const toKeyResultFormValues = (objective: ObjectiveOrDraft): KeyResultFormData[] => {
    // A draft saved before its first key result reopens on the empty row a new form has.
    if (objective.status === ObjectiveStatus.DRAFT && objective.keyResults.length === 0) {
        return [{ ...DEFAULT_KEY_RESULT }];
    }

    // Each key result keeps its id, so the update synchronizes by id. The API sends an absent
    // optional value as `null`, which the form schema only accepts as `undefined`.
    return [...objective.keyResults]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((keyResult) => ({
            id: keyResult.id,
            title: keyResult.title,
            unit: keyResult.unit,
            currencySymbol: keyResult.currencySymbol ?? undefined,
            startValue: keyResult.startValue,
            targetValue: keyResult.targetValue,
            currentValue: keyResult.currentValue,
            responsibleEmployeeId: keyResult.responsibleEmployeeId ?? undefined,
        }));
};

/**
 * The form values of a saved objective. A draft may not have its employees or its dates yet, and
 * only the people listed by name come back as chips: the members of an attribute come back with it.
 */
export const toObjectiveFormValues = (objective: ObjectiveOrDraft): ObjectiveFormData => {
    const participantIds = objective.individualParticipantEmployeeIds ?? [];

    return {
        title: objective.title,
        description: objective.description ?? '',
        // A draft may have been saved before its pillar question was answered: it asks again.
        pillarIds:
            objective.status === ObjectiveStatus.DRAFT &&
            objective.pillarIds.length === 0 &&
            !objective.hasNoPillar
                ? null
                : objective.pillarIds,
        employeeIds: objective.ownerEmployeeId
            ? [objective.ownerEmployeeId, ...participantIds]
            : participantIds,
        participantAttributes: objective.participantAttributes ?? [],
        competencyIds: objective.competencyIds ?? [],
        startDate: objective.startDate ?? '',
        endDate: objective.endDate ?? '',
        keyResults: toKeyResultFormValues(objective),
        reminders: objective.reminders ?? [],
    };
};

/**
 * The responsible each key result was saved with, by key result id, unless they are named on the
 * objective: only a member of an attribute can leave it outside the form, and the server keeps them.
 */
export const toSavedResponsibles = (objective: ObjectiveOrDraft): ReadonlyMap<string, string> => {
    const namedIds = new Set([
        objective.ownerEmployeeId,
        ...(objective.individualParticipantEmployeeIds ?? []),
    ]);

    return new Map(
        objective.keyResults.flatMap((keyResult) =>
            keyResult.responsibleEmployeeId && !namedIds.has(keyResult.responsibleEmployeeId)
                ? [[keyResult.id, keyResult.responsibleEmployeeId] as const]
                : [],
        ),
    );
};

export type SavedKeyResult = Pick<
    KeyResult,
    'unit' | 'startValue' | 'targetValue' | 'currentValue'
>;

export const toSavedKeyResults = (
    objective: ObjectiveOrDraft,
): ReadonlyMap<string, SavedKeyResult> =>
    new Map(objective.keyResults.map((keyResult) => [keyResult.id, keyResult]));

const isProgressUnmoved = (keyResult: SavedKeyResult, saved: SavedKeyResult | undefined) =>
    !!saved &&
    saved.unit === keyResult.unit &&
    saved.startValue === keyResult.startValue &&
    saved.targetValue === keyResult.targetValue &&
    saved.currentValue === keyResult.currentValue;

interface ObjectiveRequestInput {
    data: ObjectiveFormData;
    /** The key results the form was loaded with, by id; empty for a new objective. */
    savedKeyResults: ReadonlyMap<string, SavedKeyResult>;
    status: ObjectiveStatusValue;
    /** Empty for free dates. */
    objectivePeriodId: string;
    pillarIds: string[];
}

/**
 * The request a submitted form sends. The first employee owns the objective and the others take
 * part in it; a second employee or an attribute makes it a team objective.
 */
export const toObjectiveRequest = ({
    data,
    savedKeyResults,
    status,
    objectivePeriodId,
    pillarIds,
}: ObjectiveRequestInput): CreateObjectiveRequest => {
    const [ownerEmployeeId, ...participantEmployeeIds] = data.employeeIds;
    const type =
        participantEmployeeIds.length > 0 || data.participantAttributes.length > 0
            ? ObjectiveType.TEAM
            : ObjectiveType.INDIVIDUAL;
    // A draft keeps the key results the user started, not the empty row the form opens with.
    const keyResults =
        status === ObjectiveStatus.DRAFT
            ? data.keyResults.filter((keyResult) => !isUntouchedKeyResult(keyResult))
            : data.keyResults;

    return {
        status,
        pillarIds,
        hasNoPillar: data.pillarIds?.length === 0,
        objectivePeriodId: objectivePeriodId || null,
        ownerEmployeeId: ownerEmployeeId ?? null,
        participantEmployeeIds,
        participantAttributes: data.participantAttributes,
        title: data.title,
        description: data.description,
        type,
        startDate: data.startDate || null,
        endDate: data.endDate || null,
        competencyIds: type === ObjectiveType.INDIVIDUAL ? data.competencyIds : [],
        keyResults: keyResults.map((keyResult) => {
            // Enter submits without leaving the field, and a start or target edited after the
            // current value can leave it outside the range: bound it once more on the way out.
            const currentValue = clampToKeyResultRange({
                startValue: keyResult.startValue,
                targetValue: keyResult.targetValue,
                value: keyResult.currentValue,
            });
            // Progress is also recorded from the objective's page while this form is open: a
            // key result the form did not measure differently leaves its value out, and the
            // server keeps the one it has.
            const isCurrentValueUnmoved =
                !!keyResult.id &&
                isProgressUnmoved(
                    { ...keyResult, currentValue },
                    savedKeyResults.get(keyResult.id),
                );

            return {
                id: keyResult.id,
                title: keyResult.title,
                unit: keyResult.unit,
                currencySymbol:
                    keyResult.unit === KeyResultUnit.MONETARY
                        ? keyResult.currencySymbol
                        : undefined,
                startValue: keyResult.startValue,
                targetValue: keyResult.targetValue,
                ...(isCurrentValueUnmoved ? {} : { currentValue }),
                responsibleEmployeeId:
                    type === ObjectiveType.TEAM ? keyResult.responsibleEmployeeId : undefined,
            };
        }),
        reminders: data.reminders,
    };
};
